import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Card, List, Space, Tag, Typography } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ReloadOutlined, WarningFilled } from '@ant-design/icons';

import { getEstadoSat } from '../api/estadoSat';
import type { EstadoSatResponse, ServicioSatEstado } from '../api/estadoSat';
import { Loader, ErrorState } from '../components';

const { Title, Paragraph, Text } = Typography;

type StatusSatTipo = 'ok' | 'warn' | 'err';

// HTTP >= 400 no es "todo ok" aunque disponible=true: el servicio responde
// pero con error. Va a advertencia, no a verde.
function estadoServicio(s: ServicioSatEstado): StatusSatTipo {
  if (!s.disponible) return 'err';
  if (s.http_status == null || s.http_status < 400) return 'ok';
  return 'warn';
}

export function EstadoSat() {
  const [data, setData]       = useState<EstadoSatResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const fetchEstado = useCallback((isRefresh: boolean) => {
    if (isRefresh) setChecking(true);
    setError(null);
    return getEstadoSat()
      .then(setData)
      .catch(() => setError('No se pudo consultar el estado de los servicios del SAT.'))
      .finally(() => {
        setLoading(false);
        setChecking(false);
      });
  }, []);

  useEffect(() => { fetchEstado(false); }, [fetchEstado]);

  if (loading) return <Loader tip="Consultando Servicios del SAT..." />;
  if (error) return <ErrorState message={error} onRetry={() => fetchEstado(false)} />;

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
        <Title level={3} style={{ margin: 0 }}>Estado del SAT</Title>
        <Button icon={<ReloadOutlined />} loading={checking} onClick={() => fetchEstado(true)}>
          Actualizar
        </Button>
      </Space>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Disponibilidad de los Servicios Web del SAT (Descarga Masiva y Validación de CFDI)
      </Paragraph>

      {data && (
        <>
          {(() => {
            const todosOk = data.servicios.every(s => estadoServicio(s) === 'ok');
            const hayError = data.servicios.some(s => estadoServicio(s) === 'err');
            return (
              <Alert
                style={{ marginBottom: 16 }}
                showIcon
                type={todosOk ? 'success' : hayError ? 'error' : 'warning'}
                message={todosOk ? 'Todos los Servicios del SAT Están Disponibles' : hayError ? 'Uno o Más Servicios del SAT No Responden' : 'Uno o Más Servicios del SAT Responden con Errores'}
                description={`Última verificación: ${dayjs(data.checado_at).format('DD/MM/YYYY HH:mm:ss')} (zona local ${dayjs().format('Z')})`}
              />
            );
          })()}

          <Card>
            <List
              dataSource={data.servicios}
              renderItem={(s) => {
                const tipo = estadoServicio(s);
                return (
                <List.Item>
                  <List.Item.Meta
                    avatar={tipo === 'ok'
                      ? <CheckCircleFilled style={{ color: '#198754', fontSize: 20 }} />
                      : tipo === 'warn'
                        ? <WarningFilled style={{ color: '#ffc107', fontSize: 20 }} />
                        : <CloseCircleFilled style={{ color: '#dc3545', fontSize: 20 }} />}
                    title={<Text strong>{s.nombre}</Text>}
                    description={<Text type="secondary" style={{ fontSize: 12 }}>{s.url}</Text>}
                  />
                  <Space>
                    {s.http_status !== null && <Tag>{`HTTP ${s.http_status}`}</Tag>}
                    <Tag color={tipo === 'ok' ? 'green' : tipo === 'warn' ? 'gold' : 'red'}>{`${s.latencia_ms} ms`}</Tag>
                    {s.error && <Tag color="red">{s.error}</Tag>}
                  </Space>
                </List.Item>
                );
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
}
